import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Fragment, useContext } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import DonutCustomLegend from '../../components/ApexCharts/CustomLegends/DonutCustomLegend';
import DonutChart from '../../components/ApexCharts/DonutChart';
import Blockquote from '../../components/Blockquote';
import Button from '../../components/Button/Button';
import Carousel from '../../components/Carousel';
import ChartCard from '../../components/ChartCard';
import DefaultCard from '../../components/DefaultCard';
import FingerTipsLinks from '../../components/FingertipsLinks/FingertipsLinks';
import GenericCard from '../../components/GenericCards';
import VerticalSteps from '../../components/VerticalSteps';
import TotalRewardsHalf from '../widgets/dashboards/TotalRewardsHalf';

import {
  PensionIndexProvider,
  PensionIndexStore,
} from '../../providers/pensionIndex';
import {
  ACCOUNT_ACTIVITY_ROUTE,
  PENSION_PROFILE_ROUTE,
  PENSION_ROUTE,
  RETIREMENT_PLANNING_ROUTE,
  RETIRE_ROUTE,
  TOTAL_REWARDS_ROUTE,
} from '../../routes/types';
import {
  addOrRemoveDaysToGivenDate,
  addOrRemoveYearsToGivenDate,
  DateFormats,
  formatDate,
  formatPrice,
} from '../../utils/utils';
import PopoverWithLinks from '../widgets/PopoverLinks/PopoverLinks';
import OtherCompanyBenefits from '../widgets/dashboards/OtherCompanyBenefits';

const linksData = [
  {
    linkHeader: 'Account History',
    route: ACCOUNT_ACTIVITY_ROUTE,
  },

  {
    linkHeader: 'Pension Profile',
    route: PENSION_PROFILE_ROUTE,
  },
];

const accruedPensionData = [
  {
    title: 'Cash Balance Plan',
    balance: 53185.62,
    benefit: 355.21,
    payAge: 65,
  },

  {
    title: 'Qualified Plan',
    balance: 'n/a',
    benefit: 2643.98,
    payAge: 65,
  },

  {
    title: 'Non-Qualified Plan',
    balance: 'n/a',
    benefit: 602.95,
    payAge: 65,
  },
];

const dataCompanyContributions = {
  grid: {
    padding: {
      left: 0,
    },
  },
  legend: {
    show: false,
  },

  dataLabels: {
    enabled: false,
  },
  responsive: [
    {
      breakpoint: 480,
      options: {
        grid: {
          padding: {
            left: 0,
          },
        },
        legend: {
          show: false,
        },
        title: {},
      },
    },
  ],
  colors: ['#074CBC', '#F48462', '#07B4A0', '#EEA968', '#92E3A9'],
  series: [95072, 15905, 14791, 6500, 0],
  labels: [
    'Compensation',
    'Health and Welfare',
    'Retirement',
    'Equity',
    'Other Benefits',
  ],

  tooltip: {
    enabled: false,
  },

  plotOptions: {
    pie: {
      donut: {
        labels: {
          show: true,
          name: {
            offsetY: 10,
          },
          total: {
            show: true,
            showAlways: true,
            fontSize: '18px',
            fontFamily: 'Roboto, Arial, sans-serif',
            fontWeight: 500,
            label: `$132,268`,
            formatter: function (a: any) {
              return '';
            },
          },
        },
      },
    },
  },
};

const milestonesData = [
  {
    title: 'Earliest Retirement Date',
    age: 50,
    date: formatDate(new Date(), DateFormats.US),
    firstSubTitle: {
      title: 'Qualified Plan',
      amount: 993.25,
      plans: [
        { title: 'Cash Balance Plan', amount: 176.43 },
        { title: 'Legacy Plan', amount: 816.82 },
      ],
    },
    secondSubTitle: { title: 'Non-Qualified Plan ', amount: 243.18 },
  },
  {
    title: 'Unreduced Early Retirement Date',
    date: formatDate(
      addOrRemoveYearsToGivenDate(new Date(), 5),
      DateFormats.US
    ),
    age: 55,
    firstSubTitle: {
      title: 'Qualified Plan',
      amount: 1942.9,
      plans: [
        { title: 'Cash Balance Plan', amount: 248.1 },
        { title: 'Legacy Plan', amount: 1694.8 },
      ],
    },
    secondSubTitle: { title: 'Non-Qualified Plan ', amount: 412.78 },
  },
  {
    title: 'Normal Retirement Date',
    date: formatDate(
      addOrRemoveYearsToGivenDate(new Date(), 15),
      DateFormats.US
    ),
    age: 65,
    firstSubTitle: {
      title: 'Qualified Plan',
      amount: 2643.98,
      plans: [
        { title: 'Cash Balance Plan', amount: 355.21 },
        { title: 'Legacy Plan', amount: 2288.77 },
      ],
    },
    secondSubTitle: { title: 'Non-Qualified Plan ', amount: 602.95 },
  },
];

const PensionIndexView = () => {
  const { fingertipLinks, companiesMessages, otherCompanyBenefits } =
    useContext(PensionIndexStore) || {};
  const navigate = useNavigate();

  let date = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  return (
    <Container className={'cnd-custom-container-large mb-12 px-lg-9 px-xl-0'}>
      <Row>
        <Col className="mt-7 mt-lg-11 col-12 col-lg-7">
          <DefaultCard
            className="h-100 flex-column flex-sm-row "
            variant="blue-motif"
            leftHeading={
              <div className="text-sm-20-lg-32">
                The easiest way to retire is online
              </div>
            }
            leftSubheading={
              <Fragment>
                <div className="text-sm-12-lg-14 fw-light mb-7">
                  You are eligible to commence your Pension Benefits. When you
                  are ready to retire, use the button below to start the
                  process.
                </div>
              </Fragment>
            }
            button={
              <Button
                className="align-self-end"
                variant="primary-shadow"
                size="sm"
                onClick={() =>
                  navigate(RETIRE_ROUTE.replace(':option', 'start'))
                }
              >
                Retire Online
              </Button>
            }
          />
        </Col>
        <Col className="mt-7 mt-lg-11 col-12 col-lg-5">
          <DefaultCard
            className="flex-column h-100"
            variant="blue"
            leftHeading={<div className="text-sm-20-lg-32">Current Events</div>}
            leftSubheading={
              <Container className="p-0 mb-7">
                <Row className="text-sm-12-lg-14 p-0">
                  <Col className=" text-nowrap col-12 fw-bold">
                    Pension Benefit Commencement
                  </Col>
                </Row>
                <Row className="text-sm-12-lg-14 p-0">
                  <Col className=" text-nowrap col-12 fw-light">
                    {`Deadline: ${formatDate(
                      addOrRemoveDaysToGivenDate(new Date(), 25),
                      DateFormats.SHORT_MONTH_DAY_YEAR
                    )}`}
                  </Col>
                </Row>
                <Row className="text-sm-12-lg-14">
                  <Col className=" text-nowrap col-12 fw-light">
                    Status: In Process
                  </Col>
                </Row>
              </Container>
            }
            button={
              <Button
                className="align-self-end"
                variant="primary-shadow"
                size="sm"
                onClick={() =>
                  navigate(RETIRE_ROUTE.replace(':option', 'required'))
                }
              >
                Edit
              </Button>
            }
          />
        </Col>
      </Row>
      <Row className="m-0">
        <Col className="p-0 pe-lg-5 col-lg-7">
          <Row>
            <Col className="mt-12 mt-lg-8 pt-lg-3 col-12 d-flex justify-content-between">
              <h4 className="text-sm-20-lg-32">Current Accrued Pension</h4>
              <div className="d-flex ">
                <PopoverWithLinks linksData={linksData} />
              </div>
            </Col>
          </Row>
          <Row className="mt-6 mt-lg-10">
            <Col className="accrued-pension">
              <GenericCard variant="white">
                <Row className="px-6 px-lg-0 mb-5">
                  <Row className="px-0 mx-0 mb-3">
                    <Col className="col-6 px-0 "></Col>
                    <Col className="px-0 text-sm-12-lg-14 fw-light text-ligther-grey">
                      Balance
                    </Col>
                    <Col className="col-1 px-0 text-center text-sm-12-lg-14 text-gray-500">
                      OR
                    </Col>
                    <Col className="px-0 text-end text-sm-12-lg-14 fw-light text-ligther-grey">
                      Monthly Benefit
                    </Col>
                  </Row>
                  {accruedPensionData.map(
                    ({ title, balance, benefit, payAge }, index) => (
                      <Row
                        key={index}
                        className={`${
                          accruedPensionData.length !== index + 1
                            ? 'border-bottom'
                            : ''
                        } p-0 m-0`}
                      >
                        <Col className="col-6">
                          <Row className="my-5 text-indigo text-sm-14-lg-16">
                            {title}
                          </Row>
                          <Row className="mb-3 text-dark-gray-50 text-sm-12-lg-14 fw-light">
                            As of {date}
                          </Row>
                        </Col>
                        <Col
                          className={`${
                            index === 0 ? 'text-dark-gray fw-normal' : ''
                          } px-0 my-5 text-sm-14-lg-20 fw-light text-light-grey-normal`}
                        >
                          {typeof balance === 'number'
                            ? `${formatPrice(Number(balance), 'en-US', {
                                currency: 'USD',
                                style: 'currency',
                                minimumFractionDigits: 0,
                              })}`
                            : balance}
                        </Col>
                        <Col className="col-1 px-0"></Col>
                        <Col className="px-0 text-end">
                          <Col className="my-5 col-12 text-sm-16-lg-20">
                            {typeof benefit === 'number'
                              ? `${formatPrice(Number(benefit), 'en-US', {
                                  currency: 'USD',
                                  style: 'currency',
                                  minimumFractionDigits: 0,
                                })}`
                              : benefit}
                          </Col>
                          <Col className="mb-3 col-12 text-dark-gray-50 text-sm-12-lg-14 fw-light">
                            Pay Age {payAge}
                          </Col>
                        </Col>
                      </Row>
                    )
                  )}
                </Row>
                <Row className="px-6 px-lg-0 ">
                  <Blockquote variant="primary">
                    <div className="fw-bold ">Did You know?</div>
                    <div>
                      If you don't make an election and you die the remaining
                      payment will be made first to your designated beneficiary
                      and if your beneficiary is dead then to your Estate.
                    </div>
                  </Blockquote>
                </Row>
              </GenericCard>
            </Col>
          </Row>
        </Col>
        <Col className="pe-lg-0 mt-12 mt-lg-8 pt-lg-3 col-12 col-lg-5">
          <FingerTipsLinks data={fingertipLinks} />
        </Col>
      </Row>

      <Row className="mt-12">
        <Col className="col-12 col-lg-6">
          <Col className="d-flex justify-content-between align-items-center mb-9">
            <h4 className="text-sm-20-lg-32 m-0">Milestones</h4>
            <Button
              variant="primary-outline"
              rightIcon={light('chevron-right')}
              size="sm"
              onClick={() => navigate(PENSION_ROUTE)}
            >
              Details
            </Button>
          </Col>
          <Col>
            <VerticalSteps data={milestonesData} />
          </Col>
        </Col>
        <Col>
          <Col>
            <Row className="px-lg-0 px-7 flex-column align-items-center">
              <Col>
              <OtherCompanyBenefits
                companyBenefits={otherCompanyBenefits}
              />
            </Col>
            </Row>
            
          </Col>
        </Col>

      </Row>
      <Row className="my-13">
        <Col className="col-12 col-lg-6">
          <ChartCard
            variant={'transperant'}
            title="Retirement Confidence"
            text={
              <p className="text-center text-lg-start  text-sub1 text-light-grey-normal fw-light lh-xs">
                Saving 8% will raise your Retirement Confidence level to 75%.
                Use the{' '}
                <Link
                  to={RETIREMENT_PLANNING_ROUTE}
                  className={
                    'fw-bold text-light-grey-normal text-decoration-none'
                  }
                >
                  Retirement Planning
                </Link>{' '}
                tool to further customize your strategy.
              </p>
            }
            button={
              <Button
                className="col-12 col-lg-7"
                variant="primary"
                rightIcon={light('person-cane')}
                size="sm"
                onClick={() => navigate(RETIREMENT_PLANNING_ROUTE)}
              >
                Retirement Planning
              </Button>
            }
          ></ChartCard>
        </Col>
        <Col className="col-12 col-lg-6 mt-13 mt-lg-0">
          <DefaultCard
            isCurrentEvent
            className="flex-column h-100"
            variant="transparent"
            leftHeading={
              <h5 className="text-fs-base fw-normal text-dark-gray text-center text-lg-start ">
                Pension Estimate
              </h5>
            }
            leftSubheading="Estimate your pension options at various ages."
            button={
              <div className="h-100 d-flex justify-content-end flex-column flex-lg-row align-items-end">
                <Button
                  className="mb-5 mb-lg-0"
                  variant="primary-outline"
                  size="sm"
                  onClick={() =>
                    navigate(PENSION_ROUTE.replace(':tab', 'saved'))
                  }
                >
                  Saved Estimates
                </Button>
                <Button
                  className="ms-0 ms-lg-5"
                  variant="primary"
                  size="sm"
                  onClick={() => navigate(PENSION_ROUTE.replace(':tab', 'new'))}
                >
                  New Estimate
                </Button>
              </div>
            }
          />
        </Col>
      </Row>
      <Row>
        <Carousel data={companiesMessages} />
      </Row>
    </Container>
  );
};

function PensionIndex() {
  return (
    <PensionIndexProvider>
      <PensionIndexView />
    </PensionIndexProvider>
  );
}

export default PensionIndex;
